import { DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ModalController, ViewWillEnter } from '@ionic/angular';
import { PlatformService } from '@panamax/app-state';
import { calendarcompatibleDateFormat } from '@shared/constants/date-formats';
import { allDaysAllowed } from '@shared/constants/dayConstraints';
import {
  areDatesEqual,
  convertStringToDate,
  datesMatch,
  defaultIsDateEnabled,
  isDateGreater,
} from '@shared/helpers/calendar.helpers';

@Component({
  selector: 'app-calendar-v2-modal',
  templateUrl: './calendar-v2-modal.component.html',
  styleUrls: ['./calendar-v2-modal.component.scss'],
})
export class CalendarV2ModalComponent implements ViewWillEnter {
  @Input() title: string;
  @Input() lastSelectedDate: Date;
  @Input() lastOrderedDate: string;
  @Input() multiple: boolean;
  @Input() timeEnabled: boolean;
  @Input() isDateEnabled: Function = defaultIsDateEnabled;
  @Input() disabledDates: Date[];
  @Input() minDate: string;
  @Input() maxDate: string;
  @Input() dayValues = allDaysAllowed;
  @Input() shouldDismissOnClear = true;
  @Input() showClearButton = true;
  @Input() prontoDates: Function;
  @Input() isProntoAvailable: boolean;

  currentDateSelected: Date;
  currentDateRangeSelected: string[];
  rangeItemOne: Date;
  rangeItemTwo: Date;

  constructor(
    private datepipe: DatePipe,
    private modalController: ModalController,
    readonly platformService: PlatformService,
  ) {}

  ionViewWillEnter(): void {
    // Using undefined instead of empty array because of console errors
    this.currentDateRangeSelected = undefined;
    this.rangeItemOne = undefined;
    this.rangeItemTwo = undefined;

    // Allow choice of bringing in previous selection for dates.
    if (this.lastSelectedDate) {
      this.currentDateSelected = this.lastSelectedDate;
    } else {
      this.currentDateSelected = undefined;
    }
  }

  public async cancel() {
    await this.modalController.dismiss();
  }

  public async clearAll() {
    if (this.shouldDismissOnClear) {
      await this.cancel();
    } else {
      if (this.multiple) {
        this.currentDateRangeSelected = undefined;
        this.rangeItemOne = undefined;
        this.rangeItemTwo = undefined;
      } else {
        this.currentDateSelected = undefined;
      }

      const datetime = document.querySelector('ion-datetime');
      datetime.reset();
    }
  }

  public handleValue($event: any) {
    if ($event.detail.value === undefined) {
      return;
    }
    if (this.multiple) {
      this.selectForRange($event.detail.value);
    } else {
      this.selectDate($event.detail.value);
    }
  }

  // Single select Code
  public selectDate(date: Date) {
    this.currentDateSelected = date;
  }

  public async sendDate() {
    await this.modalController.dismiss({
      selectedDate: this.currentDateSelected,
    });
  }

  // Date Range Selection Code
  private convertDateToString(date: Date) {
    return this.datepipe.transform(date, calendarcompatibleDateFormat);
  }

  private updateRangeDirectly() {
    // Range was not updating via databind for range selection.
    const datetime = document.querySelector('ion-datetime');
    datetime.onchange = null;
    datetime.value = this.currentDateRangeSelected;
    datetime.onchange = this.handleValue;
  }

  public selectForRange(dates: string[]) {
    if (
      dates.length === 0 ||
      datesMatch(this.currentDateRangeSelected, dates)
    ) {
      return;
    }

    if (this.rangeItemOne === undefined) {
      this.rangeItemOne = convertStringToDate(dates[0]);
      this.currentDateRangeSelected = [
        this.convertDateToString(this.rangeItemOne),
      ];
    } else if (
      this.rangeItemTwo === undefined &&
      dates.length >= 2 &&
      !areDatesEqual(new Date(dates[1]), this.rangeItemOne)
    ) {
      this.rangeItemTwo = convertStringToDate(dates[1]);
      this.currentDateRangeSelected.push(
        this.convertDateToString(this.rangeItemTwo),
      );

      let tempDate: Date;
      let maxDate: Date;

      if (isDateGreater(this.rangeItemOne, this.rangeItemTwo)) {
        maxDate = new Date(this.rangeItemOne);
        tempDate = new Date(this.rangeItemTwo);
      } else {
        maxDate = new Date(this.rangeItemTwo);
        tempDate = new Date(this.rangeItemOne);
      }
      tempDate.setDate(tempDate.getDate() + 1);

      while (isDateGreater(maxDate, tempDate)) {
        this.currentDateRangeSelected.push(this.convertDateToString(tempDate));
        tempDate.setDate(tempDate.getDate() + 1);
      }
      this.updateRangeDirectly();
    } else {
      if (dates.length > 2) {
        this.rangeItemOne = convertStringToDate(dates[dates.length - 1]);
        this.rangeItemTwo = undefined;
        this.currentDateRangeSelected = [
          this.convertDateToString(this.rangeItemOne),
        ];
      }
    }
  }

  public async sendRange() {
    const rangeToSend = [];
    this.currentDateRangeSelected.forEach(dateString => {
      rangeToSend.push(convertStringToDate(dateString));
    });
    await this.modalController.dismiss({
      selectedDateRange: rangeToSend,
    });
  }
}
